<script lang="ts">
import { defineComponent, PropType, ref, computed, nextTick } from 'vue'
import ValidationObserver from '@/components/validation/ValidationObserver.vue'
import validationOperator from '@/models/validationOperator'
import { useClientStore } from '@/stores/client'
import { useErrorStore } from '@/stores/error'
import { useScreenLockStore } from '@/stores/screenLock'
import type { Ref } from 'vue'

export default defineComponent({
  name: 'FormDefault',

  components: {
    ValidationObserver,
  },

  props: {
    /**
     * form 内に存在する入力要素の name 属性を繋げた配列
     * @description バリデーション状況を管理する際の一覧として扱う
     */
    inputScheme: {
      type: Object as PropType<Inputs | Input>,
      default: () => ({}),
    },

    /**
     * form 要素の name 属性値
     */
    name: {
      type: String,
      default: '',
    },

    /**
     * form 要素の action 属性値
     */
    action: {
      type: String,
      default: '',
    },

    /**
     * form 要素の method 属性値
     */
    method: {
      type: String as PropType<FormMethod>,
      required: true,
      validator(value: string) {
        return ['post', 'get', 'put', 'delete', 'POST', 'GET', 'PUT', 'DELETE'].includes(value)
      },
    },

    /**
     * form 要素の enctype 属性値
     * @description props としての初期値は undefined ですが、
     *              HTML の form 要素の初期値は 'application/x-www-form-urlencoded' ですので、
     *              暗黙的にこれで送られます。
     */
    enctype: {
      type: String as PropType<'application/x-www-form-urlencoded' | 'multipart/form-data' | 'text/plain'>,
      default: undefined,
    },

    /*
     * 画面レンダリング時の初回バリデーションをするかどうか
     */
    initValidation: {
      type: Boolean,
      default: false,
    },

    /**
     * submit が実際に走る前に発火する任意の関数
     * @description submit 前に何か処理を行いたい場合に使用
     */
    beforeAction: {
      type: Function,
      default() {
        return
      }
    }
  },

  emits: ['error', 'validated'],

  setup(props, { emit }) {
    // store
    const clientStore = useClientStore()
    const errorStore = useErrorStore()
    const screenLock = useScreenLockStore()

    const formEl: Ref<HTMLFormElement | undefined> = ref()
    const { validationObserver, validate } = validationOperator()
    const initialized = ref(false)

    /**
     * HTML がサポートしている form メソッドかどうか
     *
     * @description HTML の仕様上、POST と GET 以外の方法（PUT, DELETE）で送ることができないため、
     *              PUT、または DELETE で送りたい場合は、form 要素の method 属性値は 'post' で送り、
     *              form 要素内に hidden 要素で「name="_method"」「value="put（もしくは delete）"」を送ることで、任意の method で送ることができる。
     * @see https://portaltan.hatenablog.com/entry/2015/07/22/122031
     */
    const isSupportedMethod = computed<boolean>(() => {
      const method = props.method.toLowerCase()
      return !!(method === 'post' || method === 'get')
    })

    /**
     * フォームの method 属性値
     */
    const formMethod = computed(() => {
      return isSupportedMethod.value
        ? props.method
        : 'post'
    })

    const hasInputScheme = computed<boolean>(() => {
      return !!Object.keys(props.inputScheme).length
    })

    nextTick(() => {
      // 初回バリデーションが有効であれば、画面レンダリング時にバリデーションを発火させる
      if (props.initValidation) {
        validate()
      } else {
        initialized.value = true
      }
    })

    const validateForm = (e: Event): void => {
      if (hasInputScheme.value) {
        e.preventDefault()

        // ValidationObserver コンポーネントの init メソッドをつつく
        validate()
      }
    }

    const submit = (): void => {
      if (errorStore.hasFieldErrors || !props.action) return

      if (formEl.value && typeof formEl.value !== 'undefined') {
        screenLock.lockScreen({
          hasBlackOut: false,
          hasBlur: true,
          hasWaitAnimation: true,
          hasScrollLock: false,
        })

        props.beforeAction()

        formEl.value.submit()
      }
    }

    const valid = () => {
      if (initialized.value) {
        submit()
      }
    }

    const validationComoleted = () => {
      if (!initialized.value) initialized.value = true

      emit('validated')
    }

    return {
      // store
      clientStore,

      // data
      formEl,
      validationObserver,

      //computed
      isSupportedMethod,
      formMethod,

      // methods
      validateForm,
      valid,
      validationComoleted,

      // exposed method
      submit,
    }
  }
})
</script>

<template>
  <form
    ref="formEl"
    class="form"
    :name="name || undefined"
    :action="action"
    :method="formMethod"
    :enctype="enctype"
    @submit="validateForm">
    <ValidationObserver
      ref="validationObserver"
      :input-scheme="inputScheme"
      :namespace="name"
      @valid="valid"
      @error="$emit('error', $event)"
      @completed="validationComoleted">
      <slot />
      <input
        class="input"
        type="hidden"
        :name="clientStore.context.csrfParam"
        :value="clientStore.context.csrfToken">
    </ValidationObserver>

    <!--
      put、もしくは delete メソッドで Form submit する
      @see https://portaltan.hatenablog.com/entry/2015/07/22/122031
    -->
    <input
      v-if="!isSupportedMethod"
      class="input"
      type="hidden"
      name="_method"
      :value="method">
  </form>
</template>
